import React, { useState } from 'react';

import Button from 'components/Button/Button';
import { FormInput } from 'components/FormInput/FormInput';
import FormRow from 'components/FormRow/FormRow';
import { REGEX, validateInput } from 'functions/validationUtils';
import { FormErrors } from 'types/signUpForm';
import 'layouts/FormLayout/FormLayout.scss';
import { useCollection } from 'functions/hooks/useCollection';
import { Timestamp } from 'firebase/firestore';
import { useAuth } from 'context/useAuth';
import CheckBox from 'components/CheckBox/CheckBox';
import { successAsync } from 'functions/notification';
import { VehicleInterface } from 'types/VehicleInterface';

interface EditVehicleProps {
  vehicleDetails: VehicleInterface;
  closeModal?: () => any | Promise<any>,
  vehicleUpdated?: (vehicleDetails: VehicleInterface) => any | Promise<any>
}

// Create interface for vehicle form errors
interface VehicleFormErrors {
  nickname: string[];
  registrationNumber: string[];
  make: string[];
  model: string[];
  registrationYear: string[];
  vehicleColour: string[];
  engineNumber: string[];
  vinNumber: string[];
  threeDtCode: string[];
  deviceId: string[];
}

export default function EditVehicleForm({ vehicleDetails, vehicleUpdated, closeModal }: EditVehicleProps) {

  const { authorisedUser } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [vehicleDetailsState, setVehicleDetailsState] = useState(vehicleDetails);

  const [vehicleDetailsError, setVehicleDetailsError] = useState<VehicleFormErrors>({
    nickname: [],
    registrationNumber: [],
    make: [],
    model: [],
    registrationYear: [],
    vehicleColour: [],
    engineNumber: [],
    vinNumber: [],
    deviceId: [],
    threeDtCode: []
  });

  const vehiclesCollection = useCollection<any>('vehicles');

  // Create custom validateInput for vehicle
  function validateVehicleInput(id: string, value: string | number) {
    switch (id) {
      case 'nickname':
        return value ? [] : ['Please provide a valid nickname'];
      case 'registrationNumber':
        return value ? [] : ['Please provide a valid registration number'];
      case 'make':
        return value ? [] : ['Please provide a valid make'];
      case 'model':
        return value ? [] : ['Please provide a valid model'];
      case 'registrationYear':
        return REGEX.Numbers.test(`${value}`) ? [] : ['Please provide a valid year'];
      case 'vehicleColour':
        return value ? [] : ['Please provide a valid color'];
      case 'engineNumber':
        return value ? [] : ['Please provide a valid engine number'];
      case 'vinNumber':
        return value ? [] : ['Please provide a valid VIN number'];
      case 'deviceId':
        return value ? [] : ['Please provide a valid device ID'];
      default:
        return [];
    }
  }

  function handleInputValidation(e: React.ChangeEvent<HTMLInputElement>) {
    const { id, value } = e.target;
    setVehicleDetailsError((prevState) => ({
      ...prevState,
      [id]: validateVehicleInput(id, value),
    }));
  }

  function handleOnChange(
    e: React.ChangeEvent<HTMLInputElement>,
    setState: React.Dispatch<React.SetStateAction<any>>,
    setErrorState: React.Dispatch<React.SetStateAction<VehicleFormErrors>>
  ) {
    let { id, value, checked } = e.target;
    setState((prevState: any) => ({
      ...prevState,
      [id]: e.target.type === 'checkbox' || e.target.type === 'radio' ? checked : value,
    }));
    setErrorState((prevState) => ({
      ...prevState,
      [id]: validateVehicleInput(id, value),
    }));
  }

  async function handleSubmit() {
    
    setIsSubmitting(true);

    try {
      
      const now = Timestamp.now();
      const vehicle = await vehiclesCollection.getOne(vehicleDetails.id);

      if (!vehicle.details) vehicle.details = {};
      vehicle.details.nickname = vehicleDetailsState.nickname?.trim();
      vehicle.details.registrationNumber = vehicleDetailsState.registrationNumber?.replaceAll(" ", "")?.replaceAll("-", "")?.trim()?.toUpperCase();
      vehicle.details.make = vehicleDetailsState.make?.trim();
      vehicle.details.model = vehicleDetailsState.model?.trim();
      vehicle.details.engineNumber = vehicleDetailsState.engineNumber?.trim();
      vehicle.details.vinNumber = vehicleDetailsState.vinNumber?.trim();
      vehicle.deviceId = vehicleDetailsState.deviceId?.trim();

      //TODO:Sort out this madness-------
      
      vehicle.details.year = Number(vehicleDetailsState.registrationYear) || 2020;
      vehicle.details.registrationYear = Number(vehicleDetailsState.registrationYear) || 2020;

      vehicle.details.color = vehicleDetailsState.vehicleColour?.trim();
      vehicle.details.colour = vehicleDetailsState.vehicleColour?.trim();
      vehicle.details.vehicleColor = vehicleDetailsState.vehicleColour?.trim();
      vehicle.details.vehicleColour = vehicleDetailsState.vehicleColour?.trim();
      
      //---------------------------------

      if (!vehicle.integration) vehicle.integration = {};
      //vehicle.integration.threeDtCode = vehicleDetailsState.threeDtCode;
      vehicle.integration.auraDisabled = vehicleDetailsState.auraDisabled;

      if (!vehicle.audit) vehicle.audit = {};
      vehicle.audit.updatedAt = now;
      vehicle.audit.updatedBy = authorisedUser.userFullName;

      // Update other properties as needed
      await vehiclesCollection.updateOne(vehicleDetails.id, vehicle);

      // notify
      successAsync("Saved");

      if (vehicleUpdated) {
        vehicleUpdated(Object.assign(vehicleDetails, vehicleDetailsState));
      }
      
      // close
      if (closeModal) {
        closeModal();
      } else {
        window.location.reload();
      }

    } catch (error: any) {
      setVehicleDetailsError(error.response?.data?.errors || {
        nickname: [error.message],
        registrationNumber: [error.message],
        make: [error.message],
        model: [error.message],
        registrationYear: [error.message],
        vehicleColour: [error.message],
        vinNumber: [error.message],
        engineNumber: [error.message],
        deviceId: [error.message],
        threeDtCode: [error.message],
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleDelete() {
    setIsSubmitting(true);

    try {
      await vehiclesCollection.deleteOne(vehicleDetails.id);
      window.location.reload();
    } catch (error: any) {
      console.error('Failed to delete vehicle:', error);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <form className='form__edit'>
        <FormRow>
          <FormInput
            id='nickname'
            label='Nickname'
            value={vehicleDetailsState.nickname}
            onChange={(e) =>
              handleOnChange(e, setVehicleDetailsState, setVehicleDetailsError)
            }
            errorMessage={vehicleDetailsError.nickname}
            onBlur={handleInputValidation}
          />
        </FormRow>
        <FormRow>
          <FormInput
            id='registrationNumber'
            label='Registration Number'
            value={vehicleDetailsState.registrationNumber}
            onChange={(e) =>
              handleOnChange(e, setVehicleDetailsState, setVehicleDetailsError)
            }
            errorMessage={vehicleDetailsError.registrationNumber}
            onBlur={handleInputValidation}
          />
        </FormRow>
        <FormRow>
          <FormInput
            id='make'
            label='Make'
            value={vehicleDetailsState.make}
            onChange={(e) =>
              handleOnChange(e, setVehicleDetailsState, setVehicleDetailsError)
            }
            errorMessage={vehicleDetailsError.make}
            onBlur={handleInputValidation}
          />
        </FormRow>
        <FormRow>
          <FormInput
            id='model'
            label='Model'
            value={vehicleDetailsState.model}
            onChange={(e) =>
              handleOnChange(e, setVehicleDetailsState, setVehicleDetailsError)
            }
            errorMessage={vehicleDetailsError.model}
            onBlur={handleInputValidation}
          />
        </FormRow>
        <FormRow>
          <FormInput
            id='registrationYear'
            label='Year'
            value={`${vehicleDetailsState.registrationYear}`}
            onChange={(e) =>
              handleOnChange(e, setVehicleDetailsState, setVehicleDetailsError)
            }
            errorMessage={vehicleDetailsError.registrationYear}
            onBlur={handleInputValidation}
          />
        </FormRow>
        <FormRow>
          <FormInput
            id='vehicleColour'
            label='Color'
            value={vehicleDetailsState.vehicleColour}
            onChange={(e) =>
              handleOnChange(e, setVehicleDetailsState, setVehicleDetailsError)
            }
            errorMessage={vehicleDetailsError.vehicleColour}
            onBlur={handleInputValidation}
          />
        </FormRow>
        <FormRow>
          <FormInput
            id='engineNumber'
            label='Engine Number'
            value={vehicleDetailsState.engineNumber}
            onChange={(e) =>
              handleOnChange(e, setVehicleDetailsState, setVehicleDetailsError)
            }
            errorMessage={vehicleDetailsError.engineNumber}
            onBlur={handleInputValidation}
          />
        </FormRow>
        <FormRow>
          <FormInput
            id='vinNumber'
            label='VIN Number'
            value={vehicleDetailsState.vinNumber}
            onChange={(e) =>
              handleOnChange(e, setVehicleDetailsState, setVehicleDetailsError)
            }
            errorMessage={vehicleDetailsError.vinNumber}
            onBlur={handleInputValidation}
          />
        </FormRow>
        {/* <FormRow>
          <FormInput
            id='deviceId'
            label='Device ID'
            value={vehicleDetailsState.deviceId}
            onChange={(e) =>
              handleOnChange(e, setVehicleDetailsState, setVehicleDetailsError)
            }
            errorMessage={vehicleDetailsError.deviceId}
            onBlur={handleInputValidation}
          />
        </FormRow> */}
        {/* <FormRow>
          <FormInput
            id='threeDtCode'
            label='3DT Code'
            value={vehicleDetailsState.threeDtCode}
            onChange={(e) =>
              handleOnChange(e, setVehicleDetailsState, setVehicleDetailsError)
            }
            errorMessage={vehicleDetailsError.threeDtCode}
            onBlur={handleInputValidation}
          />
        </FormRow> */}
        <FormRow>
            <CheckBox
                id='auraDisabled'
                name='auraDisabled'
                label='Disable AURA'
                checked={vehicleDetailsState.auraDisabled}
                onChange={(e) => 
                    handleOnChange(e, setVehicleDetailsState, setVehicleDetailsError)
                }
                />
        </FormRow>
      </form>

      <div className='form-actions'>
        <Button
          label={isSubmitting ? 'Submitting' : 'Save'}
          small
          onClick={handleSubmit}
          disabled={isSubmitting}
        />
        {
          !isSubmitting && 
          <Button
            label='Delete Vehicle Forever'
            small
            onClick={handleDelete}
            disabled={isSubmitting}
            style={{ marginLeft: '10px', backgroundColor: isSubmitting ? 'grey' : 'red', color: 'white' }}
          />
        }
      </div>
    </>
  );
}